import React, {useEffect, useState} from 'react';
import {Button, message} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {GET_PICKUP_TASKS} from '../graphql/queries/getPickupTasks';
import {UPDATE_PICKUP_TASK_STATUS} from '../graphql/mutations/updatePickupTaskStatus';
import {
    PickupTaskQueryVariables,
    PickupTaskStatus,
    PickupTaskType,
    PickupTaskTypeWithFilter
} from '../types/PickupTaskType';
import PickupTaskFilter from '../components/PickupTaskFilter';
import PickupTaskTable from '../components/PickupTaskTable';
import PickupTaskDetailDrawer from '../components/PickupTaskDetailDrawer';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import {PickupDataProvider} from '../contexts/PickupDataContext';
import {useGeneratePickupTaskReport} from '../hooks/useGeneratePickupTaskReport';
import { usePickupStartPartialGray } from '../../../../hooks/usePickupStartPartialGray';
import AppointmentDownloadModal from '../../appointment/components/AppointmentDownloadModal';
import {BATCH_UPDATE_PICKUP_TASK_STATUS} from "../graphql/mutations/batchUpdatePickupTaskStatus";
import { useTranslation } from 'react-i18next';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';
import { ReportFileTypeEnum } from '../../../../enums/ReportFileEnum';

const PickupTaskHistory: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const getDefaultTaskDateFilter = (): Partial<PickupTaskTypeWithFilter> => {
        const [taskDateStart, taskDateEnd] = getLastWeekDateRange('YYYY-MM-DD');
        return { taskDateStart, taskDateEnd };
    };
    const [filter, setFilter] = useState<Partial<PickupTaskTypeWithFilter>>(() => getDefaultTaskDateFilter());
    const [tempFilter, setTempFilter] = useState<Partial<PickupTaskTypeWithFilter>>(() => getDefaultTaskDateFilter());
    const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const {t} = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id!;
    const { startPartialEnabled } = usePickupStartPartialGray();

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    // 添加一个持久化的选择状态，用于跨分页保存选择
    const [persistentSelection, setPersistentSelection] = useState<React.Key[]>([]);

    // 保存所有选中的PickupTask 数据，用于计算汇总信息
    const [selectedAppointments, setSelectedAppointments] = useState<Map<React.Key, PickupTaskType>>(new Map());
    // 任务下载：提交异步任务后弹提示，引导去下载中心（type=47 揽收任务）
    const [downloadVisible, setDownloadVisible] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');
    const {submit: generateTaskReport, loading: downloadLoading} = useGeneratePickupTaskReport();

    // GraphQL mutations
    const [updatePickupTaskStatus] = useMutation(UPDATE_PICKUP_TASK_STATUS);

    // GraphQL mutations
    const [batchUpdatePickupTaskStatus] = useMutation(BATCH_UPDATE_PICKUP_TASK_STATUS);

    // 构建查询变量
    const buildFilter = (warehouseId: string | null): PickupTaskQueryVariables => {
        // 从filter中提取后端需要的字段，排除前端UI专用的字段
        const {startPointName, endPointName, ...backendFilter} = filter;

        return {
            filter: {
                warehouseId: warehouseId || undefined,
                ...backendFilter
            },
            pageRequest: {
                page: currentPage,
                pageSize: pageSize
            }
        };
    };

    // 下载沿用列表当前的筛选条件（buildFilter 的 filter 部分），保证导出内容与页面所见一致
    const handleDownload = async () => {
        if (!warehouseId) {
            message.error(t('pickup.appointment.messages.pleaseSelectWarehouse') || 'Please select a warehouse first');
            return;
        }
        const fileName = await generateTaskReport(
            buildFilter(warehouseId).filter as unknown as Record<string, unknown>,
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        if (fileName) {
            setDownloadFileName(fileName);
            setDownloadVisible(true);
        }
    };

    const {loading, error, data, refetch} = useQuery(GET_PICKUP_TASKS, {
        variables: buildFilter(warehouseId),
        fetchPolicy: 'cache-and-network'
    });

    useEffect(() => {
        refetch(buildFilter(warehouseId));
    }, [currentPage, pageSize, filter, warehouseId, refetch]);

    // 当页面数据加载完成后，根据持久化选择更新当前页面的选择状态
    useEffect(() => {
        if (!loading && pickupTasks.length > 0) {
            // 找出当前页面中应该被选中的行
            const currentPageKeys = pickupTasks
                .filter((task: PickupTaskType) => persistentSelection.includes(task.id))
                .map((task: PickupTaskType) => task.id);

            setSelectedRowKeys(currentPageKeys);

            // 更新选中的Task数据
            const newSelectedAppointments = new Map(selectedAppointments);
            pickupTasks.forEach((task: PickupTaskType) => {
                if (persistentSelection.includes(task.id)) {
                    newSelectedAppointments.set(task.id, task);
                }
            });
            setSelectedAppointments(newSelectedAppointments);
        }
    }, [data, loading]);

    const handleFilterChange = (newFilter: Partial<PickupTaskTypeWithFilter>) => {
        setTempFilter(newFilter);
    };

    const handleFilter = () => {
        setFilter(tempFilter);
        setCurrentPage(1);
    };

    const handleReset = () => {
        const defaultFilter = getDefaultTaskDateFilter();
        setTempFilter(defaultFilter);
        setFilter(defaultFilter);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
        }
    };

    const handleShowDetail = (taskId: string) => {
        setEditingTaskId(taskId);
        setDrawerVisible(true);
    };

    const handleCancelTask = async (taskId: string) => {
        try {
            // 使用已存在的updateTaskStatus mutation
            await updatePickupTaskStatus({
                variables: {
                    taskId,
                    status: PickupTaskStatus.CANCELLED,
                    warehouseId
                }
            });
            message.success(t('pickup.pickupTask.messages.taskCancelledSuccessfully') || '');
            refetch();
        } catch (error) {
            message.error(t('pickup.pickupTask.messages.failedToCancelTask') || '');
        }
    };
    if (error) {
        return <div>Error loading pickup tasks: {error.message}</div>;
    }
    const pickupTasks = data?.getPickupTasks?.data || [];
    const total = parseInt(data?.getPickupTasks?.total || '0');

    // 更新处理选择变化的函数，支持跨分页选择
    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PickupTaskType[]) => {
        // 更新当前页面的选择
        setSelectedRowKeys(newSelectedRowKeys);

        // 更新持久化选择
        const newPersistentKeys = [...persistentSelection];
        const newSelectedAppointments = new Map(selectedAppointments);

        // 处理当前页面上的选择变化（跨分页持久化）
        // 基于当前页数据 pickupTasks，对比 newSelectedRowKeys 增删
        pickupTasks.forEach((task: PickupTaskType) => {
            const id = task.id as unknown as React.Key;
            const isSelected = newSelectedRowKeys.includes(id);

            if (isSelected) {
                if (!newPersistentKeys.includes(id)) {
                    newPersistentKeys.push(id);
                }
                newSelectedAppointments.set(id, task);
            } else {
                if (newPersistentKeys.includes(id)) {
                    const index = newPersistentKeys.indexOf(id);
                    if (index > -1) {
                        newPersistentKeys.splice(index, 1);
                    }
                    newSelectedAppointments.delete(id);
                }
            }
        });

        // 确保把本次回调提供的 newSelectedRows 也加入（应对非当前页也被选中的情况）
        newSelectedRows.forEach((row) => {
            const id = row.id as unknown as React.Key;
            if (!newPersistentKeys.includes(id)) {
                newPersistentKeys.push(id);
            }
            newSelectedAppointments.set(id, row);
        });

        // 更新持久化选择状态
        setPersistentSelection(newPersistentKeys);
        setSelectedAppointments(newSelectedAppointments);
    };

    // 批量发布Task
    const handleBatchPublishTask = async (): Promise<void> => {
        // 使用跨页持久化的选择作为提交的任务ID
        const taskIds = persistentSelection.map((k) => String(k));

        if (!taskIds || taskIds.length === 0) {
            message.warning('Please select at least one task');
            return;
        }

        try {
            const {data: mutationData} = await batchUpdatePickupTaskStatus({
                variables: {taskIds, status: PickupTaskStatus.PUBLISHED, warehouseId},
            });

            const result = mutationData?.batchUpdateTaskStatus;
            const successCount = result?.successCount ?? (result?.successIds?.length || 0);
            const failedCount = result?.failedCount ?? (result?.failedIds?.length || 0);

            if (failedCount === 0) {
                message.success(`Published ${successCount} tasks successfully`);
            } else {
                // 组织失败原因提示
                const failures = (result?.failures || []) as Array<{
                    taskId: string;
                    taskNumber?: string | null;
                    reasons: string[]
                }>;
                const details = failures.map(f => `${f.taskNumber || f.taskId}: ${f.reasons.join(', ')} is missed`);
                const head = `Published ${successCount} tasks, ${failedCount} failed`;
                const preview = details.slice(0, 5).join(' | ');
                const suffix = details.length > 5 ? ` (and ${details.length - 5} more...)` : '';
                message.error(`${head}. ${preview}${suffix}`);
                // 控制台输出完整详情方便排查
                // eslint-disable-next-line no-console
                console.table(failures);
            }

            // 从持久化选择中移除成功发布的任务
            if (successCount > 0) {
                const successSet = new Set(result.successIds as string[]);
                const nextPersistent = persistentSelection.filter((id) => !successSet.has(String(id)));
                const nextSelectedAppointments = new Map(selectedAppointments);
                // 同步移除成功项的缓存详情
                Array.from(successSet).forEach((id) => nextSelectedAppointments.delete(id));
                setPersistentSelection(nextPersistent);
                setSelectedAppointments(nextSelectedAppointments);
                // 同步行选中状态（仅对当前页可见的项生效）
                const nextSelectedRowKeys = selectedRowKeys.filter((id) => !successSet.has(String(id)));
                setSelectedRowKeys(nextSelectedRowKeys);
            }

            // 刷新列表
            refetch(buildFilter(warehouseId));
        } catch (e: any) {
            console.error('Failed to publish tasks:', e);

            let errorMessage = 'Publish failed';
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                const graphQLError = e.graphQLErrors[0];
                if (graphQLError.extensions?.message) {
                    errorMessage = graphQLError.extensions.message;
                } else if (graphQLError.message) {
                    errorMessage = graphQLError.message;
                }
            } else if (e.message) {
                errorMessage = e.message;
            }
            message.error(errorMessage);
        }
    };

    return (
        <PickupDataProvider>
            <div className="bg-white rounded-lg shadow-sm p-6">
                {/* 筛选条件 */}
                <PickupTaskFilter
                    filter={tempFilter}
                    onFilterChange={handleFilterChange}
                />

                {/* 已选择货量显示 */}
                <div className="flex justify-between items-center text-sm text-gray-600">
                    <div></div>
                    <div className="flex gap-2">
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleReset}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.reset')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleBatchPublishTask}
                            disabled={persistentSelection.length === 0 }
                            hidden={!hasResource([ResourceCode.menu.PICKUP_TASK_OPERATE])}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.pickupTask.buttons.publishTask')}</span>
                        </Button>
                        {/* 揽收任务下载是本期新增能力，按 hub 灰度。后端 generatePickupTaskDownloadReport
                            另有一道拦截——前端隐藏按钮 ≠ 有权限 */}
                        {startPartialEnabled(currentWarehouse?.id) && (
                            <Button
                                onClick={handleDownload}
                                loading={downloadLoading}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('pickup.pickupTask.buttons.download')}</span>
                            </Button>
                        )}
                    </div>
                </div>

                {/* 任务列表 */}
                <PickupTaskTable
                    data={pickupTasks}
                    loading={loading}
                    onCancelTask={handleCancelTask}
                    onShowDetail={handleShowDetail}
                    selectedRowKeys={selectedRowKeys}
                    onSelectChange={handleSelectChange}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={total}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                    paginationProps={{
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />

                <PickupTaskDetailDrawer
                    visible={drawerVisible}
                    onClose={() => {
                        setDrawerVisible(false);
                        setEditingTaskId(null);
                    }}
                    taskId={editingTaskId}
                    onSave={() => refetch(buildFilter(warehouseId))}
                />
                {/* 原先这里硬编码 45：2026-09-28 后端 ReportTypeEnum.PICKUP_TASK 因与
                    REALTIME_INVOICE_ORDER_DETAIL(45) 撞号被挪到 47，这个裸数字没跟着改，
                    下载中心就按 45 过滤——后端白名单对未知类型静默返回空，全链路不报错，
                    表现为「点了下载没报错但下载中心是空的」。
                    改用 ReportFileTypeEnum：下载中心的筛选项、类型列标签本来就用它，
                    统一走枚举后再有撞号只需改一处。 */}
                <AppointmentDownloadModal
                    visible={downloadVisible}
                    onClose={() => setDownloadVisible(false)}
                    fileName={downloadFileName}
                    downloadType={ReportFileTypeEnum.PICKUP_TASK}
                />
            </div>
        </PickupDataProvider>
    );
};

export default PickupTaskHistory; 